import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../i18n/useP';
import { listVatRates, upsertVatRate, deleteVatRate } from '../api/tenants';
import { legalNormalVatRate } from '../utils/vatLegalRates';

// Décimal en base (0.081) ↔ % en UI (8.1).
const decToPct = (d) => (d == null || d === '' ? '' : String(+(Number(d) * 100).toFixed(2)));
const pctToDec = (s) => {
  if (s == null || String(s).trim() === '') return null;
  const n = parseFloat(String(s).replace(/[\s']/g, '').replace(',', '.'));
  return isNaN(n) ? null : n / 100;
};

const NOW = new Date().getFullYear();
const range = (a, b) => Array.from({ length: b - a + 1 }, (_, i) => a + i);

/**
 * Grille des taux de TVA par année (source unique year-aware).
 * Années en blocs qui passent à la ligne (pas de scroll), un seul type de taux
 * selon la méthode du tenant. Démarre à l'année de comparaison (1ʳᵉ année − 1).
 * Valeur saisie = bleu (charte) ; taux reporté = gris. Chaque année supprimable.
 */
export default function VatRatesGrid({ tenantId, method = 'effective', firstAnalysisYear = null, addToast }) {
  const { t } = useTranslation();
  const p = useP();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [extraYears, setExtraYears] = useState([]);
  const [removedYears, setRemovedYears] = useState([]);

  const compYear = (firstAnalysisYear ? firstAnalysisYear - 1 : NOW - 1);
  const endYear = Math.max(NOW + 5, compYear + 6);
  const defaultYears = range(compYear, endYear);

  const field = method === 'tdfn' ? 'vat_rate_tdfn' : 'vat_rate';
  const otherField = method === 'tdfn' ? 'vat_rate' : 'vat_rate_tdfn';
  const rowLabel = method === 'tdfn' ? p('Taux dette fiscale nette (%)') : p('Taux TVA (%)');

  async function reload() {
    setLoading(true);
    const res = await listVatRates(tenantId);
    setRows(res.data || []);
    setLoading(false);
  }

  useEffect(() => { if (tenantId) reload(); /* eslint-disable-next-line */ }, [tenantId]);

  // Colonnes = plage par défaut ∪ années en base ∪ ajoutées, − retirées, triées.
  const years = Array.from(
    new Set([...defaultYears, ...rows.map(r => r.year), ...extraYears])
  ).filter(y => !removedYears.includes(y)).sort((a, b) => a - b);

  const ownValue = (year) => {
    const r = rows.find(x => x.year === year);
    return r ? r[field] : null;
  };
  const inheritedValue = (year) => {
    let val = null;
    rows.filter(r => r[field] != null && r.year <= year)
      .sort((a, b) => a.year - b.year)
      .forEach(r => { val = r[field]; });
    return val;
  };
  // Défaut affiché (gris) quand aucune saisie propre : effectif → dernier override
  // saisi, sinon le TAUX LÉGAL de l'année (barème AFC) ; TDFN → dernier connu seul
  // (pas de barème légal).
  const defaultValue = (year) => {
    const inh = inheritedValue(year);
    if (inh != null) return inh;
    return method === 'tdfn' ? null : legalNormalVatRate(year);
  };

  async function saveCell(year, pctStr) {
    const existing = rows.find(r => r.year === year);
    const newDec = pctToDec(pctStr);
    const otherVal = existing ? existing[otherField] : null;
    if (newDec == null && otherVal == null) {
      if (existing) {
        const res = await deleteVatRate(tenantId, year);
        if (res.data) await reload();
        else addToast && addToast(res.error || p('Erreur à la suppression.'), 'error');
      }
      return;
    }
    const payload = method === 'tdfn'
      ? { vat_rate: otherVal, vat_rate_tdfn: newDec }
      : { vat_rate: newDec, vat_rate_tdfn: otherVal };
    const res = await upsertVatRate(tenantId, year, payload);
    if (res.data) await reload();
    else addToast && addToast(res.error || p('Erreur sur le taux.'), 'error');
  }

  function addYear() {
    const y = (years.length ? Math.max(...years) : NOW) + 1;
    if (y > 2100) { addToast && addToast(t('phrases:p_ffd88f10f1'), 'error'); return; }
    setRemovedYears(prev => prev.filter(x => x !== y));
    setExtraYears(prev => [...prev, y]);
  }

  async function removeYear(year) {
    if (rows.find(r => r.year === year)) {
      const res = await deleteVatRate(tenantId, year);
      if (!res.data) { addToast && addToast(res.error || p('Erreur à la suppression.'), 'error'); return; }
    }
    setExtraYears(prev => prev.filter(x => x !== year));
    setRemovedYears(prev => [...prev, year]);
    await reload();
  }

  if (loading) return <div className="vat-grid-empty">{p('Chargement…')}</div>;

  return (
    <div className="vat-grid">
      <div className="vat-grid-label">
        {rowLabel}
        <span className="vat-grid-sub">
          {method === 'tdfn'
            ? p('Saisir le taux accordé par l’AFC ; reporté (en gris) vers les années suivantes.')
            : p('Taux légaux appliqués automatiquement (en gris) ; saisir seulement si taux différent (ex. hébergement 3,8 %).')}
        </span>
      </div>
      <div className="vat-cells">
        {years.map(y => (
          <VatCell
            key={y}
            year={y}
            ownPct={decToPct(ownValue(y))}
            inheritedPct={decToPct(defaultValue(y))}
            onSave={saveCell}
            onRemove={removeYear}
          />
        ))}
      </div>
      <div className="vat-grid-add">
        <button type="button" className="settings-btn settings-btn--ghost" onClick={addYear}>
          {p('+ Ajouter une année')}
        </button>
      </div>
    </div>
  );
}

function VatCell({ year, ownPct, inheritedPct, onSave, onRemove }) {
    const { t } = useTranslation();
const initial = ownPct !== '' ? ownPct : inheritedPct;
  const isInherited = ownPct === '' && inheritedPct !== '';
  const [v, setV] = useState(initial);
  useEffect(() => { setV(ownPct !== '' ? ownPct : inheritedPct); }, [ownPct, inheritedPct]);

  const cls = ownPct !== '' ? 'is-own' : (isInherited ? 'is-inherited' : '');

  return (
    <div className="vat-cell">
      <div className="vat-cell-head">
        <span className="vat-cell-year">{year}</span>
        <button type="button" className="vat-cell-del" title={t('phrases:p_2a3232f5c1')} onClick={() => onRemove(year)}>✕</button>
      </div>
      <input
        type="text" inputMode="decimal"
        className={`settings-input settings-input--year ${cls}`}
        value={v}
        onChange={e => setV(e.target.value)}
        onBlur={() => { if (v !== initial) onSave(year, v); }}
      />
    </div>
  );
}
